import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import ResultsView from "./ResultsView";
import Modal from "../Modal";
import SelectBasedInput from "../SelectBasedInput";
import AutoComplete from "../AutoComplete";
import Pagination from "../Pagination";

const offset = 10;

const InputsModal = ({
  show,
  inputs,
  onSearch,
  searchButtonName,
  resultsFieldNames,
  onSelect,
  closeCallback,
  onAutoCompleteChange,
  autoCompleteData,
  autoCompleteValue,
  label,
  labelClassName,
  placeholder,
  id,
  disabler,
  downloadImg,
  sortImg,
  modalMessages,
  onScrollBottom,
  autoCompleteOptions
}) => {
  const [inputValue, changeInputValue] = useState({});
  const [searchResults, setSearchResults] = useState([]);
  const [sortASC, setSortASC] = useState(true);
  const [pageStart, setPageStart] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(show);
  const baseSearchData = useRef([]);

  useEffect(() => {
    const inputsInitialState = {};
    inputs.forEach(v => {
      inputsInitialState[v.name] = { type: v.type, value: "" };
    });
    changeInputValue(inputsInitialState);
  }, [inputs]);

  const changePage = number => {
    setCurrentPage(number);
    const startNum = (number - 1) * offset;
    setPageStart(startNum);
    let newData = [...baseSearchData.current];
    newData = newData.slice(startNum, startNum + offset);
    setSearchResults(newData);
  };

  const sortBy = sortKey => {
    baseSearchData.current.sort((a, b) => (b[sortKey] > a[sortKey] ? 1 : -1));
    let newSearchResults = [...baseSearchData.current];
    newSearchResults = newSearchResults.slice(0, offset);
    setPageStart(1);
    setSortASC(!sortASC);
    setSearchResults(newSearchResults);
  };

  const close = () => {
    setShowModal(false);
    if (closeCallback) {
      closeCallback();
    }
  };

  const renderModalBody = () => {
    const renderData = inputs.map(v => (
      <SelectBasedInput
        options={v.options}
        value={inputValue}
        firstDisabler={disabler}
        label={v.label}
        key={v.label}
        onChange={e => {
          const { operator, text } = e.target.value;
          changeInputValue({ ...inputValue, [v.name]: { type: v.type, value: { operator, text } } });
        }}
      />
    ));
    renderData.push(
      <button
        type="button"
        id="uiKitSearchBtn"
        key="find"
        onClick={() => {
          onSearch(inputValue).then(res => {
            baseSearchData.current = res;
            setSearchResults(res.slice(pageStart, offset));
          });
        }}
      >
        {searchButtonName}
      </button>
    );

    if (searchResults.length) {
      renderData.push(
        <ResultsView
          key="results"
          searchResults={searchResults}
          resultsFieldNames={resultsFieldNames}
          inputs={inputs}
          close={close}
          onSelect={onSelect}
          downloadImg={downloadImg}
          sortImg={sortImg}
          sortBy={sortBy}
        />
      );
    }

    if (searchResults.length > 1) {
      renderData.push(
        <div key="pagination">
          <Pagination
            totalCount={baseSearchData.current.length}
            changePage={changePage}
            pageNumber={currentPage}
            offset={offset}
          />
        </div>
      );
    }

    return <div id={id}>{renderData}</div>;
  };

  return (
    <>
      <AutoComplete
        labelClick={() => setShowModal(true)}
        label={label}
        labelClassName={labelClassName}
        placeholder={placeholder}
        value={autoCompleteValue}
        data={autoCompleteData}
        onChange={onAutoCompleteChange}
        onScrollBottom={onScrollBottom}
        options={autoCompleteOptions}
      />
      {Object.keys(inputValue).length && (
        <Modal show={showModal} render={renderModalBody} closeDialog={() => close()} messages={modalMessages} />
      )}
    </>
  );
};

InputsModal.displayName = "InputsModal";

InputsModal.defaultProps = {
  show: false,
  inputs: [],
  searchButtonName: "Find",
  resultsFieldNames: [],
  closeCallback: null,
  autoCompleteData: [],
  autoCompleteValue: { code: "", description: "" },
  labelClassName: null,
  placeholder: "",
  id: "",
  disabler: null,
  downloadImg: null,
  sortImg: null,
  modalMessages: {},
  onScrollBottom: null,
  autoCompleteOptions: {}
};

InputsModal.propTypes = {
  /** Defines if Modal should be open */
  show: PropTypes.bool,
  /** Name of the Find button */
  searchButtonName: PropTypes.string,
  /** Action for searching */
  onSearch: PropTypes.func.isRequired,
  /** Results list data for AutoComplete */
  autoCompleteData: PropTypes.array,
  /** Label for displaying input and popup on click */
  label: PropTypes.string.isRequired,
  /** Label class name */
  labelClassName: PropTypes.string,
  /** id for popup body */
  id: PropTypes.string,
  /** Value to check of the input should be disabled */
  disabler: PropTypes.string,
  /** Autocomplete placeholder */
  placeholder: PropTypes.string,
  /** Icon for download image */
  downloadImg: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  /** Icon for image sort */
  sortImg: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  /** AutoComplete value */
  autoCompleteValue: PropTypes.object,
  /** Callback function when autocomplete changes */
  onAutoCompleteChange: PropTypes.func.isRequired,
  /** Action for selecting data from search results  */
  onSelect: PropTypes.func.isRequired,
  /** Provide additional functionality to onClose event */
  closeCallback: PropTypes.func,
  /** List of fields to show after search */
  resultsFieldNames: PropTypes.array,
  /** Inputs for selectBasedInputs component */
  inputs: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired
    })
  ),
  /** List of needed messages to be displayed */
  modalMessages: PropTypes.shape({
    title: PropTypes.string.isRequired,
    body: PropTypes.string,
    close: PropTypes.string,
    yes: PropTypes.string,
    no: PropTypes.string
  }),
  /** Event callback when scrolling to bottom */
  onScrollBottom: PropTypes.func,
  /** Options for autocomplete */
  autoCompleteOptions: PropTypes.object
};

export default InputsModal;
